import * as Sentry from "@sentry/react";

/**
 * Configuration de Sentry pour le monitoring des erreurs
 * 
 * IMPORTANT: Configurer VITE_SENTRY_DSN dans .env
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";
  
  // Ne pas initialiser Sentry en dev local (optionnel)
  if (environment === "development" && !dsn) {
    console.log("🔍 Sentry disabled in development (no DSN configured)");
    return;
  }
  
  if (!dsn) {
    console.warn("⚠️ Sentry DSN not configured. Set VITE_SENTRY_DSN in .env");
    return;
  }
  
  Sentry.init({
    dsn,
    environment,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session Replay pour voir ce que l'utilisateur a fait avant l'erreur
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Taux d'échantillonnage des transactions (performance)
    tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% en prod, 100% en dev
    
    // Taux d'échantillonnage des replays
    replaysSessionSampleRate: 0.1, // 10% des sessions
    replaysOnErrorSampleRate: 1.0, // 100% des sessions avec erreur
    
    // Ignorer certaines erreurs communes
    ignoreErrors: [
      // Erreurs réseau
      "Network request failed",
      "NetworkError",
      "Failed to fetch",
      
      // Erreurs browser
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      
      // Erreurs extensions navigateur
      "chrome-extension://",
      "moz-extension://",
    ],
    
    // Filtrer les breadcrumbs (logs de navigation)
    beforeBreadcrumb(breadcrumb) {
      // Ne pas logger les clics sur les boutons de dev
      if (breadcrumb.category === "ui.click" && breadcrumb.message?.includes("devtools")) {
        return null;
      }
      return breadcrumb;
    },
    
    // Enrichir les événements avec du contexte
    beforeSend(event) {
      // Ajouter des infos custom
      if (event.user) {
        // Ne jamais envoyer de données sensibles (passwords, tokens, etc.)
        delete event.user.email; // Optionnel: garder l'email ou non
      }
      
      return event;
    },
  });
  
  console.log(`✅ Sentry initialized (${environment})`);
}

/**
 * Capturer une erreur manuellement
 */
export function captureError(error: Error | string, context?: Record<string, any>) {
  if (typeof error === "string") {
    Sentry.captureMessage(error, {
      level: "error",
      extra: context,
    });
  } else {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

/**
 * Ajouter du contexte utilisateur
 */
export function setUserContext(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Supprimer le contexte utilisateur (logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Ajouter des tags custom
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Ajouter du contexte custom
 */
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, context);
}
